/**
 * RateMyPluckBanner — customer dashboard banner for Item #6.
 *
 * Renders ABOVE the credit card on `/dashboard/customer` (the
 * overview index). Surfaces completed-but-unreviewed bookings so the
 * customer's nudge to rate is on the FIRST surface they land on
 * after sign-in — the in-app counterpart to the email deep-link from
 * Backlog B.2.x.
 *
 * Data source — SINGLE call to `/bookings/mine?view=customer&upcoming=false`.
 * The endpoint is enriched with `has_review` (Item #6 backend) so the
 * banner does NOT need a parallel `/reviews/mine` fetch (drift bait).
 * Already-reviewed bookings are excluded server-side via the `reviews`
 * unique index — we mirror that, never override.
 *
 * Auto-open discipline (one-time per booking, client-side):
 *   - localStorage key `pp_review_autoopen_shown` stores a JSON object
 *     `{ "<booking_id>": true, ... }` keyed by booking id. We DO NOT
 *     scope by user id — localStorage is per-device per-browser, and
 *     booking ids are uuids (no cross-user collision possible).
 *   - On first dashboard mount for a given completed-unreviewed
 *     booking, the row is briefly highlighted + scrolled into view,
 *     and its booking id is persisted into the shown-set. Subsequent
 *     mounts skip the auto-open animation entirely; the row is still
 *     in the persistent banner until the customer reviews it.
 *   - localStorage is per-device. Backend-tracked auto-open state is
 *     intentionally NOT implemented — see Item #6 brief.
 *
 * Multi-booking edge: only the MOST RECENT completed-unreviewed
 * booking auto-opens. The others sit in the persistent banner. Sorted
 * by `completed_at` desc (falls back to `service_date` desc when the
 * timestamp is missing — every recently-completed booking has one).
 *
 * Threshold for collapse: if the customer has 5+ unreviewed
 * completed bookings, we render a count-only summary instead of the
 * row list (per brief — "5+ is sane"). Each row otherwise links to
 * `/review/{booking_id}` — the existing auth-gated route from
 * Backlog B.2.x. No tokens. No secrets. Single submit endpoint.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Sparkles, Star } from "lucide-react";

const AUTOOPEN_STORAGE_KEY = "pp_review_autoopen_shown";
const COLLAPSE_THRESHOLD = 5;

function loadShownSet() {
  try {
    const raw =
      typeof window !== "undefined" &&
      window.localStorage.getItem(AUTOOPEN_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistShownSet(map) {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(AUTOOPEN_STORAGE_KEY, JSON.stringify(map));
    }
  } catch {
    /* swallow — storage full / private mode */
  }
}

function formatServiceDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function pickMostRecent(rows) {
  if (!rows || rows.length === 0) return null;
  const ranked = [...rows].sort((a, b) => {
    const ka = a.completed_at || a.service_date || "";
    const kb = b.completed_at || b.service_date || "";
    if (ka === kb) return 0;
    return kb.localeCompare(ka);
  });
  return ranked[0];
}

export default function RateMyPluckBanner() {
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  // The booking id that just auto-opened (focus / scroll target).
  // null = no auto-open this render cycle.
  const [autoOpenedId, setAutoOpenedId] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get("/bookings/mine", {
          params: { view: "customer", upcoming: false },
        });
        if (cancelled) return;
        const unreviewed = (data || []).filter(
          (b) => b.status === "completed" && b.has_review === false,
        );
        setRows(unreviewed);

        // Auto-open: most-recent completed-unreviewed booking that has
        // NOT been auto-opened on this device before.
        const target = pickMostRecent(unreviewed);
        if (target) {
          const shown = loadShownSet();
          if (!shown[target.id]) {
            setAutoOpenedId(target.id);
            shown[target.id] = true;
            persistShownSet(shown);
          }
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll the auto-opened row into view once mounted.
  useEffect(() => {
    if (!autoOpenedId) return;
    const node = rowRefs.current[autoOpenedId];
    if (node && typeof node.scrollIntoView === "function") {
      // The dashboard already starts at the top of the viewport, so
      // smooth scroll is sufficient — no jank.
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [autoOpenedId]);

  const total = rows.length;
  const collapsed = total >= COLLAPSE_THRESHOLD;
  const visibleRows = useMemo(
    () => (collapsed ? [] : rows),
    [collapsed, rows],
  );

  // Empty state — render nothing. No "you have no reviews" placeholder.
  if (!loaded || total === 0) return null;

  return (
    <Alert
      className="mb-8 rounded-3xl border-pp-orange/40 bg-pp-orange/10"
      data-testid="rate-my-pluck-banner"
    >
      <Sparkles className="h-5 w-5 text-pp-orange" aria-hidden="true" />
      <AlertTitle
        className="font-heading text-lg font-bold text-pp-text"
        data-testid="rate-my-pluck-banner-title"
      >
        How was your Pluck?
      </AlertTitle>
      <AlertDescription className="mt-1 text-sm text-pp-text">
        {collapsed ? (
          <div
            className="mt-2 flex flex-wrap items-center justify-between gap-3"
            data-testid="rate-my-pluck-banner-collapsed"
          >
            <span data-testid="rate-my-pluck-banner-count">
              {total} cleanups awaiting your review
            </span>
            <Button
              asChild
              size="sm"
              className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid="rate-my-pluck-banner-see-all"
            >
              <Link to="/dashboard/customer/bookings">See all</Link>
            </Button>
          </div>
        ) : (
          <div
            className="mt-2 space-y-2"
            data-testid="rate-my-pluck-banner-list"
          >
            {total > 1 ? (
              <p
                className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                data-testid="rate-my-pluck-banner-summary"
              >
                {total} cleanups awaiting your review
              </p>
            ) : null}
            {visibleRows.map((b) => {
              const isAutoOpen = b.id === autoOpenedId;
              const providerName =
                b.provider_business_name || "your PooPartner";
              const dateLabel = formatServiceDate(b.service_date);
              return (
                <div
                  key={b.id}
                  ref={(el) => {
                    if (el) rowRefs.current[b.id] = el;
                  }}
                  data-testid={`rate-my-pluck-row-${b.id}`}
                  data-autoopened={isAutoOpen ? "true" : "false"}
                  className={[
                    "flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-pp-surface px-4 py-3 transition",
                    isAutoOpen
                      ? "border-pp-orange ring-2 ring-pp-orange animate-pulse"
                      : "border-border",
                  ].join(" ")}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-pp-text">
                      {providerName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {dateLabel}
                    </span>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                    data-testid={`rate-my-pluck-cta-${b.id}`}
                  >
                    <Link to={`/review/${b.id}`}>
                      <Star className="mr-1 h-3.5 w-3.5" />
                      Rate it
                    </Link>
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
